import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import {
	IAdjustment,
	IGetCommissionCyclePositionsStatus,
	IGetPayrollData,
	IGetPayrollSubmit,
	IGetPayrollSubmitList,
	IGetSellers,
	IRetrieveAdjustmentCount,
} from "@/Washouts/types/PayrollClosing/queries";
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";
import { IWashoutApprovedByPosition } from "@/Washouts/types/PayrollClosing/CommissionCycleClosingReviewers";

export async function getCommissionCyclePayPeriods(locationId: string, id: string) {
	const query = gql`
		query getCommissionCycle($locationId: String!, $id: String!) {
			getCommissionCycle(locationId: $locationId, id: $id) {
				_id
				dataUpdatedAt
				status
				startDate
				endDate
				payPeriods {
					_id
					title
					startDate
					endDate
					inReview
				}
			}
		}
	`;

	const variables = {
		locationId,
		id,
	};

	try {
		const response: GraphqlQueryResponse<"getCommissionCycle", ICommissionCycle> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getCommissionCycle;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getCommissionCyclePositionsStatus(commissionCycledId: string, payPeriodId: string, locationId: string) {
	const query = gql`
		query getCommissionCycle($id: String!, $locationId: String!) {
			getCommissionCycle(id: $id, locationId: $locationId) {
				_id
				payPeriods {
					_id
					payPeriodProviders {
						status
						providerPayPeriodId
					}
					positionStatus {
						position
						status
					}
				}
			}
		}
	`;

	const variables = {
		id: commissionCycledId,
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getCommissionCycle", IGetCommissionCyclePositionsStatus> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		const payPeriod = response.data.data?.getCommissionCycle?.payPeriods?.find((period: { _id: string }) => period._id === payPeriodId);
		return payPeriod?.positionStatus;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getPayrollPreview(locationId: string, commissionCycleId: string, payPeriodId: string, positions: string[]) {
	const query = gql`
		query getPayrollData($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $positions: [String]!) {
			getPayrollData(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				positions: $positions
			) {
				columns {
					label
					columnType
				}
				data {
					row {
						label
						value
					}
				}
				position
			}
		}
	`;

	const variables = { locationId, commissionCycleId, payPeriodId, positions };

	try {
		const response: GraphqlQueryResponse<"getPayrollData", IGetPayrollData[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getPayrollData;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getPayrollSubmit(locationId: string, payrollSubmitId: string) {
	const query = gql`
		query getPayrollSubmit($locationId: String!, $payrollSubmitId: String!) {
			getPayrollSubmit(locationId: $locationId, payrollSubmitId: $payrollSubmitId) {
				_id
				columns {
					label
					columnType
				}
				data {
					row {
						label
						value
					}
				}
				status
				statusDate
				submitter {
					_id
					fullName
				}
			}
		}
	`;
	const variables = {
		locationId,
		payrollSubmitId,
	};

	try {
		const response: GraphqlQueryResponse<"getPayrollSubmit", IGetPayrollSubmit> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getPayrollSubmit;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getPayrollSubmitList(locationId: string, payPeriodId: string) {
	const query = gql`
		query getPayrollSubmitList($locationId: String!, $payPeriodId: String!) {
			getPayrollSubmitList(locationId: $locationId, payPeriodId: $payPeriodId) {
				_id
				status
				statusDate
				errors
				submitter {
					_id
					fullName
				}
				position
				companyId
				displayId
				providerPayPeriodId
				dateRange
			}
		}
	`;
	const variables = {
		locationId,
		payPeriodId,
	};

	try {
		const response: GraphqlQueryResponse<"getPayrollSubmitList", IGetPayrollSubmitList[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getPayrollSubmitList;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getAdjustments(locationId: string, payPeriodId: string) {
	const query = gql`
		query getAdjustments($locationId: String!, $payPeriodId: String!) {
			getAdjustments(locationId: $locationId, payPeriodId: $payPeriodId) {
				_id
				locationId
				payPeriodId
				adjustments {
					_id
					amount
					checkNumber
					adjustmentType
					createdBy {
						_id
						fullName
					}
					createdOn
					description
					lastModifiedBy
					lastModifiedOn
					recipient {
						_id
						fullName
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		payPeriodId,
	};

	try {
		const response: GraphqlQueryResponse<"getAdjustments", IAdjustment> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response?.data?.data?.getAdjustments;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getRecipientCandidates(locationId: string, position: string[] = [], status = "ACTIVE") {
	const query = gql`
		query getUsers($locationId: String!, $filter: UserFilterInput) {
			getUsers(locationId: $locationId, filter: $filter) {
				_id
				fullName
			}
		}
	`;

	const variables = {
		locationId,
		filter: {
			position,
			status,
			locationId,
		},
	};

	try {
		const response: GraphqlQueryResponse<"getUsers", IGetSellers[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data?.data?.getUsers;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function getWashoutApprovedByPosition(locationId: string, payPeriodId: string, positions: string[]) {
	const query = gql`
		query getWashoutApprovedByPosition($locationId: String!, $payPeriodId: String!, $positions: [String]!) {
			getWashoutApprovedByPosition(locationId: $locationId, payPeriodId: $payPeriodId, positions: $positions) {
				locationId
				payPeriodId
				positions {
					position
					userIds
				}
			}
		}
	`;

	const variables = {
		locationId,
		payPeriodId,
		positions,
	};

	try {
		const response: GraphqlQueryResponse<"getWashoutApprovedByPosition", IWashoutApprovedByPosition> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getWashoutApprovedByPosition;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}

export async function retrieveAdjustmentCount(locationId: string) {
	const query = gql`
		query getLocation($locationId: String!) {
			getLocation(locationId: $locationId) {
				_id
				locationId
				prefix
				adjustmentCount
			}
		}
	`;

	const variables = { locationId };

	try {
		const response: GraphqlQueryResponse<"getLocation", IRetrieveAdjustmentCount> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getLocation;
	} catch (err) {
		errorLogger(err as Error);
		return null;
	}
}
